(ns app.front.view.permit.permit-number-editor
  "Editor for editing the permit number (in practice the licence number for a renewed licence)
  from the verdict draft view.
  Also allows user to link renewal to an existing licence entity if one with the same number is found."
  (:require [app.domain.group.config.model :as config-model]
            [app.domain.licence.model :as licence-model]
            [app.domain.verdict.model :as verdict-model]
            [app.domain.workspace.model :as workspace-model]
            [app.front.entities :as entities]
            [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.util :as futil]
            [core.db.entity.util :as eu]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))


(defn save-new-number-signal
  "Utility for saving the new permit number (and :workspace/municipal-case-id to match)
  If this is a licence renewal, should only be called when the licence-to-be-renewed cannot be found in the database.
  If the existing licence is found, it should instead be linked to the renewal (see `link-licence-signal` below)"
  [verdict-id target-id new-value]
  [::process/signal
   target-id
   [:process.signal/edit-permit-number {:value new-value}]
   {:on-success [::update-saved-changes verdict-id]}])


(defn link-licence-signal
  "See docstring for `save-new-number-signal` (the other possible end result for this editor) above."
  [verdict-id target-id licence-db-id]
  [::process/signal
   target-id
   [:process.signal/link-renewal-to-licence {:licence-id licence-db-id}]
   {:on-success [::update-saved-changes verdict-id]}])


(rf/reg-sub ::data :-> ::data)


(rf/reg-sub ::updated-value
  :<- [::data]
  (fn [data [_ verdict-id]]
    (get-in data [verdict-id :updated-value])))


(rf/reg-sub ::fetching-licence?
  :<- [::data]
  (fn [data [_ verdict-id]]
    (boolean (get-in data [verdict-id :fetching-licence?]))))


(rf/reg-sub ::existing-licence
  :<- [::data]
  (fn [data [_ verdict-id]]
    (when-let [entity-id (get-in data [verdict-id :existing-licence-id])]
      (<sub [::entities/entity entity-id]))))


(rf/reg-sub ::unsaved-changes?
  (fn [[_ verdict-id]]
    [(rf/subscribe [::updated-value verdict-id])
     (rf/subscribe [::entities/entity verdict-id])])
  (fn [[edited-permit-number verdict]]
    (and (some? edited-permit-number)
         (not= edited-permit-number
               (verdict-model/verdict-or-licence-number verdict)))))


(rf/reg-event-db ::update-value
  (fn [db [_ verdict-id value]]
    (assoc-in db [::data verdict-id :updated-value] value)))


(rf/reg-event-db ::clear
  (fn [db [_ verdict-id]]
    (p/dissoc-in db [::data verdict-id])))


(rf/reg-event-db ::cancel-confirmation
  (fn [db [_ verdict-id]]
    (p/dissoc-in db [::data verdict-id :existing-licence-id])))


(rf/reg-event-fx ::fetch-existing-licence-for-confirmation
  (fn [{:keys [db]} [_ group-id verdict-id target-id]]
    {:db           (assoc-in db [::data verdict-id :fetching-licence?] true)
     :comm/command {:command      :licence/get
                    :method       :get
                    :data         {:group-id   group-id
                                   :licence-id (get-in db [::data verdict-id :updated-value])}
                    :on-success   [::fetch-existing-licence-for-confirmation.success verdict-id]
                    :on-not-found [::fetch-existing-licence-for-confirmation.not-found verdict-id target-id]}}))


(rf/reg-event-fx ::fetch-existing-licence-for-confirmation.success
  ;; Existing licence found, therefore ask the user if this is the licence they are trying to renew
  (fn [{:keys [db]} [_ verdict-id status {:keys [entities]}]]
    {:dispatch [::entities/merge-entity-db status entities]
     :db       (update-in db
                          [::data verdict-id]
                          merge
                          {:fetching-licence?   false
                           :existing-licence-id (-> (vals entities)
                                                    (p/find-first (eu/is-type-fn :entity.type/licence))
                                                    :db/id)})}))


(rf/reg-event-fx ::fetch-existing-licence-for-confirmation.not-found
  ;; Licence not found; therefore no licence to link to; therefore save the value normally
  (fn [{:keys [db]} [_ verdict-id target-id]]
    {:db       (assoc-in db [::data verdict-id :fetching-licence?] false)
     :dispatch (->> (get-in db [::data verdict-id :updated-value])
                    (save-new-number-signal verdict-id target-id))}))


(rf/reg-event-fx ::update-saved-changes
  (fn [_ [_ verdict-id]]
    {:fx [[:dispatch [:notifications/add :toast :success :success :changes-saved]]
          [:dispatch [::clear verdict-id]]]}))


(defn licence-info-row
  "Displays a single attribute of information from the licence (status, expiration date, etc.)"
  [tr licence attribute & [value-format-fn label-tr-key]]
  (when-let [value (get licence attribute)]
    [:div.bx--row.my-2
     [:div.bx--col-xs-4.bold
      (if label-tr-key
        (tr [label-tr-key])
        (tr [attribute]))]
     [:div.bx--col-xs-8
      (cond-> value
        value-format-fn value-format-fn)]]))


(defn- licence-renewal-confirmation-modal
  "When an existing licence already has the given number, ask the user with this modal
  whether they want to in fact renew the existing licence or cancel the number change and pick another number"
  [{:keys [tr lang format-date verdict-id target-id existing-licence]}]
  (let [localize-config #(config-model/localized-name % lang)
        licence-row     (fn [& args]
                          (apply conj [licence-info-row tr existing-licence] args))]
    [ui/confirm-modal
     {:tr           tr
      :id           "permit-number-editor-confirmation"
      :modal-title  (tr [:licence/renewal-link-confirmation])
      :open?        (some? existing-licence)
      :on-close     #(>evt [::cancel-confirmation verdict-id])
      :on-yes-click #(>evt (link-licence-signal verdict-id target-id (:db/id existing-licence)))}
     [:div.mb-4 (tr [:licence/renewal-link-help])]
     [licence-row :licence/id]
     [licence-row :licence/category-group-config localize-config :workspace/category]
     [licence-row :licence/primary-property :property/id :property/id]
     [licence-row :licence/primary-property (comp :address/full-address :property/primary-address) :licence/address]
     [licence-row :domain/state #(tr [%]) :licence/status]
     [licence-row :licence/valid-from format-date]
     [licence-row :licence/valid-to format-date]]))


(defn editor
  "Allows an authority user to manually update the number on a permit (or licence) draft.
  In practice only visible for legacy licence renewals due to state machine restrictions on the signal."
  [{:keys                                     [tr lang format-date workspace verdict]
    {:workspace/keys [permit-type]}           :workspace
    {:verdict/keys [municipal-permit-number]} :verdict}]
  (when (<sub [::process/app-signal-permitted? [:process.signal/edit-permit-number]])
    (let [verdict-id       (:db/id verdict)
          target-id        (-> workspace workspace-model/workspace-target :db/id)
          group-id         (-> workspace :domain/authority-group :group/id)
          updated-value    (<sub [::updated-value verdict-id])
          fetching?        (<sub [::fetching-licence? verdict-id])
          existing-licence (<sub [::existing-licence verdict-id])
          disabled?        (boolean (or fetching?
                                        ;; This is a licence possibly found with the inputted number
                                        existing-licence
                                        ;; This is an actual existing licence entity linked to
                                        ;; this workspace, i.e. this is a renewal.
                                        (and (-> workspace :licence/_workspaces seq)
                                             ;; If the renewal needs a new number anyway, we allow the number
                                             ;; editor to be used.
                                             (not (licence-model/new-number-to-be-assigned? workspace)))))
          save-disabled?   (or disabled? (not (<sub [::unsaved-changes? verdict-id])))]
      [:<>
       [licence-renewal-confirmation-modal
        (util/mmap verdict-id tr lang format-date target-id existing-licence)]
       ;; The same spacing of inputs and buttons as e.g. app.front.view.permit.form/date-input
       [:div.bx--row.items-end
        [:div.bx--col-xs-5.bx--col-md-8.bx--col-lg-6.bx--col-xxl-4
         [ui/text-input
          {:id         "permit-number-editor"
           :value      (or updated-value municipal-permit-number)
           :label-text (tr [:verdict/municipal-permit-number])
           :disabled   disabled?
           :on-change  #(>evt [::update-value verdict-id (futil/tval %)])}]]
        [:div.bx--col-xs-7.bx--col-md-4.bx--col-lg-6.bx--col-xxl-8.flex.flex-wrap.gap-4
         (when-not disabled?
           [:<>
            (cc/button {:kind     :tertiary
                        :size     :field
                        :disabled save-disabled?
                        :on-click #(>evt [::clear verdict-id])}
                       (tr [:clear]))
            (cc/button {:kind     :primary
                        :size     :field
                        :disabled save-disabled?
                        ;; For renewals without specified original licence,
                        ;; first check if one can be found so the linkage can be made by the user
                        :on-click #(if (= permit-type :permit-type/L)
                                     (>evt [::fetch-existing-licence-for-confirmation group-id verdict-id target-id])
                                     (>evt (save-new-number-signal verdict-id target-id updated-value)))}
                       (tr [:save]))
            (when (<sub [::process/app-signal-permitted? [:process.signal/assign-municipal-case-id-from-next-year]])
              (cc/button {:kind     :tertiary
                          :size     :field
                          :disabled (<sub [::process/signal-processing?
                                           :process.signal/assign-municipal-case-id-from-next-year])
                          :on-click #(>evt [::process/signal target-id
                                            [:process.signal/assign-municipal-case-id-from-next-year]])}
                         (tr [:verdict/assign-number-from-next-year])))])
         (when fetching?
           [:div.pb-1
            (cc/inline-loading {:description (tr [:loading])})])]]])))
